import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import './CoursesPage.css';
import './NotesPage.css';
import DotGrid from './DotGrid';
import Navbar from './components/Navbar';

const IS_BOT = typeof navigator !== 'undefined' && (
  /Lighthouse|PageSpeed|Googlebot|Chrome-Lighthouse|PTST|HeadlessChrome|Moto G Power/i.test(navigator.userAgent) ||
  navigator.webdriver ||
  window._phantom || window.callPhantom || window.__nightmare ||
  (typeof window !== 'undefined' && (
    (window.innerWidth === 1350 && window.innerHeight === 940) ||
    window.navigator.userAgent.includes('Lighthouse')
  ))
);

export default function AccountDetailsPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Profile');
  const [purchases, setPurchases] = useState([]);
  const [resetMessage, setResetMessage] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (session) {
          setUser(session.user);
          fetchPurchases(session.user.id);
        } else {
          navigate('/login');
        }
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const checkUser = async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error || !user) {
        navigate('/login');
      } else {
        setUser(user);
        fetchPurchases(user.id);
      }
    } catch (err) {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchases = async (userId) => {
    const { data } = await supabase.from('purchases').select('*').eq('user_id', userId);
    
    if (data && data.length > 0) {
      const noteIds = data.filter(p => p.item_type === 'note').map(p => p.item_id);
      const courseIds = data.filter(p => p.item_type === 'course').map(p => p.item_id);
      const mpqIds = data.filter(p => p.item_type === 'mpq').map(p => p.item_id);

      let details = [];
      if (noteIds.length > 0) {
        const { data: notes } = await supabase.from('notes').select('id, title, thumbnail_url').in('id', noteIds);
        details = [...details, ...(notes || []).map(n => ({...n, item_type: 'note'}))];
      }
      if (mpqIds.length > 0) {
        const { data: mpqs } = await supabase.from('mpqs').select('id, title, thumbnail_url').in('id', mpqIds);
        details = [...details, ...(mpqs || []).map(n => ({...n, item_type: 'mpq'}))];
      }
      if (courseIds.length > 0) {
        const { data: courses } = await supabase.from('courses').select('id, title, thumbnail_url').in('id', courseIds);
        details = [...details, ...(courses || []).map(n => ({...n, item_type: 'course'}))];
      }

      const enriched = data.map(p => {
        const detail = details.find(d => d.id === p.item_id && d.item_type === p.item_type);
        return { ...p, ...detail };
      });
      // Sort by created_at desc (newest first)
      enriched.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      setPurchases(enriched);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const handleResetPassword = async () => {
    if (!user?.email) return;
    setResetMessage('Sending...');
    const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
      redirectTo: window.location.origin + '/account',
    });
    if (error) {
      setResetMessage('Error: ' + error.message);
    } else {
      setResetMessage('Password reset email sent! Please check your inbox.');
    }
    // Clear message after 5 seconds
    setTimeout(() => setResetMessage(''), 5000);
  };

  const handleDeleteAccount = () => {
    alert("For security reasons, to permanently delete your account and all associated data, please email us at engimitra@gmail.com from your registered email address. Our team will process the deletion within 24 hours.");
  };

  if (loading) {
    return <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}><p style={{ color: 'var(--muted)' }}>Loading account details...</p></div>;
  }

  if (!user) return null; // Redirecting

  const meta = user?.user_metadata || user?.raw_user_meta_data || {};
  
  const activeCoursesCount = purchases.filter(p => p.item_type === 'course').length;
  const purchasedNotesCount = purchases.filter(p => p.item_type === 'note' || p.item_type === 'mpq').length;

  return (
    <div className="courses-page-container document-explorer-page">
      <div className="cp-bg-wrapper">
        {!IS_BOT && (
          <DotGrid
            dotSize={2}
            gap={15}
            baseColor="#004f54"
            activeColor="#00e5c8"
            proximity={120}
            shockRadius={250}
            shockStrength={5}
            resistance={750}
            returnDuration={1.5}
          />
        )}
      </div>

      <Navbar />

      <main className="cp-main cp-dashboard-main">
        <section className="cp-header-section" style={{ paddingBottom: '2rem' }}>
          <div>
            <h1 className="cp-title"><span className="cp-gradient-text">My</span> Account</h1>
            <p className="cp-subtitle">
              Manage your personal information and access your premium resources.
            </p>
          </div>
        </section>

        <div className="cp-layout cp-dashboard-layout">
          <aside className="cp-sidebar cp-dashboard-sidebar">
            <div className="glass-panel cp-explorer">
              <h3 style={{ color: 'var(--ink)' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '1.2rem' }}>manage_accounts</span>
                  DASHBOARD
                </span>
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
                <button 
                  className="cp-branch-btn" 
                  onClick={() => setActiveTab('Profile')}
                  style={{ background: activeTab === 'Profile' ? 'rgba(0, 229, 200, 0.1)' : 'transparent', borderRadius: '8px', padding: '0.5rem' }}
                >
                  <div className="cp-branch-icon">
                    <span className="material-symbols-outlined" style={{ color: activeTab === 'Profile' ? 'var(--cyan)' : 'var(--muted)', fontSize: '1.2rem' }}>person</span>
                    <span style={{ color: activeTab === 'Profile' ? 'var(--cyan)' : 'var(--muted)', fontWeight: activeTab === 'Profile' ? 'bold' : 'normal' }}>Profile</span>
                  </div>
                </button>
                <button 
                  className="cp-branch-btn" 
                  onClick={() => setActiveTab('Purchases')}
                  style={{ background: activeTab === 'Purchases' ? 'rgba(0, 229, 200, 0.1)' : 'transparent', borderRadius: '8px', padding: '0.5rem' }}
                >
                  <div className="cp-branch-icon">
                    <span className="material-symbols-outlined" style={{ color: activeTab === 'Purchases' ? 'var(--cyan)' : 'var(--muted)', fontSize: '1.2rem' }}>shopping_bag</span>
                    <span style={{ color: activeTab === 'Purchases' ? 'var(--cyan)' : 'var(--muted)', fontWeight: activeTab === 'Purchases' ? 'bold' : 'normal' }}>My Purchases</span>
                  </div>
                </button>
                <button 
                  className="cp-branch-btn" 
                  onClick={() => setActiveTab('Settings')}
                  style={{ background: activeTab === 'Settings' ? 'rgba(0, 229, 200, 0.1)' : 'transparent', borderRadius: '8px', padding: '0.5rem' }}
                >
                  <div className="cp-branch-icon">
                    <span className="material-symbols-outlined" style={{ color: activeTab === 'Settings' ? 'var(--cyan)' : 'var(--muted)', fontSize: '1.2rem' }}>settings</span>
                    <span style={{ color: activeTab === 'Settings' ? 'var(--cyan)' : 'var(--muted)', fontWeight: activeTab === 'Settings' ? 'bold' : 'normal' }}>Settings</span>
                  </div>
                </button>
                
                <button 
                  className="cp-branch-btn" 
                  onClick={handleLogout}
                  style={{ background: 'transparent', borderRadius: '8px', padding: '0.5rem', border: '1px dashed rgba(255, 78, 106, 0.4)', marginTop: '2rem' }}
                >
                  <div className="cp-branch-icon">
                    <span className="material-symbols-outlined" style={{ color: 'var(--rose)', fontSize: '1.2rem' }}>logout</span>
                    <span style={{ color: 'var(--rose)', fontWeight: 'bold' }}>Logout</span>
                  </div>
                </button>
              </div>
            </div>
          </aside>

          <div className="cp-dashboard-content" style={{ flexGrow: 1 }}>
            <div className="glass-panel cp-dashboard-panel" style={{ padding: '3rem', borderRadius: '24px' }}>
              {activeTab === 'Profile' && (
                <div>
                  <h2 style={{ fontSize: '1.5rem', marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span className="material-symbols-outlined" style={{ color: 'var(--cyan)' }}>badge</span>
                    Profile Information
                  </h2>
                  
                  <div className="cp-profile-summary" style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: '1.5rem', alignItems: 'center', marginBottom: '2.5rem' }}>
                    <div style={{ 
                      width: '80px', height: '80px', borderRadius: '50%', 
                      background: 'linear-gradient(135deg, var(--cyan), var(--blue))', 
                      display: 'flex', alignItems: 'center', justifyContent: 'center', 
                      fontSize: '2.5rem', fontWeight: 'bold', color: '#fff',
                      border: '3px solid var(--bg)', outline: '2px solid var(--cyan)', outlineOffset: '4px'
                    }}>
                      {(meta.full_name || user?.email || 'A').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--ink)', marginBottom: '0.2rem' }}>{meta.full_name || 'Student User'}</h3>
                      <p style={{ color: 'var(--muted)', fontSize: '1rem', marginBottom: '0.5rem' }}>{user?.email}</p>
                      <span style={{ display: 'inline-block', fontSize: '0.75rem', background: 'rgba(0, 229, 200, 0.15)', color: 'var(--cyan)', padding: '0.3rem 0.6rem', borderRadius: '4px', fontWeight: 'bold' }}>VERIFIED ACCOUNT</span>
                    </div>
                  </div>

                  <div style={{ background: 'var(--bg2)', padding: '2rem', borderRadius: '16px', border: '1px solid var(--border)' }}>
                    <h3 style={{ fontSize: '1.2rem', color: 'var(--ink)', marginBottom: '1.5rem', borderBottom: '1px solid var(--border)', paddingBottom: '0.8rem' }}>Academic Details</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                      <div>
                        <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '0.3rem' }}>College / University</p>
                        <p style={{ color: 'var(--ink)', fontWeight: 'bold', fontSize: '1.05rem' }}>{meta.college || 'Not specified'}</p>
                      </div>
                      <div>
                        <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '0.3rem' }}>Stream / Branch</p>
                        <p style={{ color: 'var(--ink)', fontWeight: 'bold', fontSize: '1.05rem' }}>{meta.stream || 'Not specified'}</p>
                      </div>
                      <div>
                        <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '0.3rem' }}>Batch</p>
                        <p style={{ color: 'var(--ink)', fontWeight: 'bold', fontSize: '1.05rem' }}>{meta.admission_year ? `${meta.admission_year} - ${meta.graduation_year}` : 'Not specified'}</p>
                      </div>
                      <div>
                        <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '0.3rem' }}>Phone Number</p>
                        <p style={{ color: 'var(--ink)', fontWeight: 'bold', fontSize: '1.05rem' }}>{meta.phone || 'Not specified'}</p>
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: '3rem' }}>
                    <h3 style={{ fontSize: '1.2rem', marginBottom: '1rem', color: 'var(--ink)' }}>Account Analytics</h3>
                    <div className="cp-dashboard-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
                      <div style={{ background: 'var(--bg2)', padding: '1.5rem', borderRadius: '12px', border: '1px solid var(--border)', textAlign: 'center' }}>
                        <span style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--cyan)' }}>{activeCoursesCount}</span>
                        <p style={{ fontSize: '0.9rem', color: 'var(--muted)', marginTop: '0.5rem' }}>Active Courses</p>
                      </div>
                      <div style={{ background: 'var(--bg2)', padding: '1.5rem', borderRadius: '12px', border: '1px solid var(--border)', textAlign: 'center' }}>
                        <span style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--rose)' }}>{purchasedNotesCount}</span>
                        <p style={{ fontSize: '0.9rem', color: 'var(--muted)', marginTop: '0.5rem' }}>Purchased Notes & MPQs</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'Purchases' && (
                <div>
                  <h2 style={{ fontSize: '1.5rem', marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span className="material-symbols-outlined" style={{ color: 'var(--cyan)' }}>inventory_2</span>
                    My Purchases
                  </h2>
                  
                  {purchases.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '4rem', color: 'var(--border)', marginBottom: '1rem' }}>shopping_cart</span>
                      <p style={{ color: 'var(--muted)', fontSize: '1.1rem' }}>You haven't purchased any materials yet.</p>
                      <Link to="/courses" className="cp-btn cp-cart-empty-cta" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginTop: '1.5rem', padding: '0.8rem 2rem', textDecoration: 'none', boxSizing: 'border-box' }}>Explore Courses</Link>
                    </div>
                  ) : (
                    <div className="cp-purchases-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
                      {purchases.map(p => {
                        const purchasedDate = p.created_at ? new Date(p.created_at) : new Date();
                        const expiryDate = new Date(purchasedDate);
                        expiryDate.setMonth(expiryDate.getMonth() + 6); // 6 months access

                        const isExpired = new Date() > expiryDate;
                        const linkTo = p.item_type === 'note' ? `/note/${p.item_id}` : p.item_type === 'mpq' ? `/mpq/${p.item_id}` : `/course/${p.item_id}`;

                        return (
                          <Link to={linkTo} key={p.id} style={{ textDecoration: 'none', display: 'block', pointerEvents: isExpired ? 'none' : 'auto' }}>
                            <div style={{ background: 'var(--bg2)', borderRadius: '16px', overflow: 'hidden', border: '1px solid var(--border)', transition: 'transform 0.2s', position: 'relative', opacity: isExpired ? 0.6 : 1 }}>
                              {isExpired && (
                                <div style={{ position: 'absolute', inset: 0, background: 'rgba(11, 13, 20, 0.7)', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '0.5rem' }}>
                                  <span className="material-symbols-outlined" style={{ fontSize: '3rem', color: 'var(--rose)' }}>lock</span>
                                  <span style={{ background: 'var(--rose)', color: '#fff', padding: '0.5rem 1rem', borderRadius: '8px', fontWeight: 'bold' }}>ACCESS EXPIRED</span>
                                </div>
                              )}
                              <div style={{ height: '140px', background: p.thumbnail_url ? `url(${p.thumbnail_url}) center/cover` : 'var(--border)' }}></div>
                              <div style={{ padding: '1.5rem' }}>
                                <span style={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'var(--cyan)', fontWeight: 'bold', letterSpacing: '1px' }}>{p.item_type}</span>
                                <h4 style={{ color: 'var(--ink)', fontSize: '1.1rem', marginTop: '0.5rem', marginBottom: '1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.title || 'Untitled Item'}</h4>
                                
                                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--border)', paddingTop: '1rem', fontSize: '0.85rem' }}>
                                  <div>
                                    <p style={{ color: 'var(--muted)', marginBottom: '0.2rem' }}>Purchased</p>
                                    <p style={{ color: 'var(--ink)' }}>{purchasedDate.toLocaleDateString()}</p>
                                  </div>
                                  <div style={{ textAlign: 'right' }}>
                                    <p style={{ color: 'var(--muted)', marginBottom: '0.2rem' }}>Valid Till</p>
                                    <p style={{ color: isExpired ? 'var(--rose)' : 'var(--cyan)', fontWeight: 'bold' }}>{expiryDate.toLocaleDateString()}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'Settings' && (
                <div>
                  <h2 style={{ fontSize: '1.5rem', marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span className="material-symbols-outlined" style={{ color: 'var(--cyan)' }}>settings</span>
                    Account Settings
                  </h2>
                  
                  {resetMessage && (
                    <div style={{ padding: '1rem', background: resetMessage.includes('Error') ? 'rgba(255, 78, 106, 0.1)' : 'rgba(0, 229, 200, 0.1)', border: `1px solid ${resetMessage.includes('Error') ? 'var(--rose)' : 'var(--cyan)'}`, borderRadius: '8px', color: resetMessage.includes('Error') ? 'var(--rose)' : 'var(--cyan)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                      {resetMessage}
                    </div>
                  )}

                    <div className="cp-settings-stack" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div className="cp-settings-row" style={{ background: 'var(--bg2)', padding: '1.5rem', borderRadius: '12px', border: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <h4 style={{ color: 'var(--ink)', marginBottom: '0.3rem' }}>Password Management</h4>
                        <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>Update your account password securely.</p>
                      </div>
                      <button className="btn-ghost" onClick={handleResetPassword}>Reset Password</button>
                    </div>

                    <div className="cp-danger-row" style={{ background: 'rgba(255, 78, 106, 0.05)', padding: '1.5rem', borderRadius: '12px', border: '1px solid var(--rose)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <h4 style={{ color: 'var(--rose)', marginBottom: '0.3rem' }}>Danger Zone</h4>
                        <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>Permanently delete your account and all associated data.</p>
                      </div>
                      <button onClick={handleDeleteAccount} style={{ background: 'none', border: '1px solid var(--rose)', color: 'var(--rose)', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>Delete Account</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Landing Page Footer */}
      <footer className="em-footer">
        <div className="fg">
          <div className="fb">
            <a href="/" className="em-logo"><span className="l1">ENGI</span><span className="l2">&lt;mitra&gt;</span></a>
            <p>Built for 1st year engineering students. Expert notes, courses, and MPQs for every major Indian university — since 2022.</p>
          </div>
          <div className="fc">
            <h5>Platform</h5>
            <ul>{["Courses", "Notes", "MPQs"].map(l => <li key={l}><Link to={l === 'Courses' ? '/courses' : `/${l.toLowerCase()}`}>{l}</Link></li>)}</ul>
          </div>
          <div className="fc">
            <h5>Community</h5>
            <ul>
              <li><a href="https://www.instagram.com/engimitra.in" target="_blank" rel="noreferrer">Instagram</a></li>
              <li><a href="https://www.facebook.com/profile.php?id=61587673103498" target="_blank" rel="noreferrer">Facebook</a></li>
              <li><a href="https://chat.whatsapp.com/BrKpOArPS3oCLPJnPBXD3Z" target="_blank" rel="noreferrer">WhatsApp Group</a></li>
            </ul>
          </div>
          <div className="fc">
            <h5>Legal</h5>
            <ul>
              <li><Link to="/privacy-policy">Privacy Policy</Link></li>
              <li><Link to="/terms">Terms</Link></li>
              <li><Link to="/refund-policy">Refund Policy</Link></li>
              <li><Link to="/contact">Contact Us</Link></li>
            </ul>
          </div>
        </div>
        <div className="fb-bottom">
          <p>© 2026 ENGImitra. All rights reserved.</p>
          <div className="soc-row">
            <a href="https://www.instagram.com/engimitra.in" target="_blank" rel="noreferrer" className="soc" aria-label="Instagram">ig</a>
            <a href="https://www.facebook.com/profile.php?id=61587673103498" target="_blank" rel="noreferrer" className="soc" aria-label="Facebook">fb</a>
            <a href="https://chat.whatsapp.com/BrKpOArPS3oCLPJnPBXD3Z" target="_blank" rel="noreferrer" className="soc" aria-label="WhatsApp Group">wa</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
